import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  BrandKitFlatTeamSettingGroupById,
  type BrandKitFlatTeamSettingGroupId,
} from "./groups";

type BrandKitSettingInput = SettingRegistryMetadataInput;

function setting(metadata: BrandKitSettingInput): BrandKitSettingInput {
  return metadata;
}

function brandKitGroup<
  const Group extends BrandKitFlatTeamSettingGroupId,
  const T extends Record<string, BrandKitSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const BrandKitRegistry = {
  ...brandKitGroup("brand_kit.images", {
    "brandKit/images/logoUrl": setting({
      label: "Store logo",
      description:
        "The merchant's logo. Used as the store logo on Redo's customer-facing emails, including repair, withdrawal and claim notifications.",
      synonyms: ["brand logo", "store logo"],
    }),
  }),
};

export type BrandKitFlatTeamSettingKey = keyof typeof BrandKitRegistry;
const BrandKitFlatTeamSettingKeys = Object.keys(
  BrandKitRegistry,
) as BrandKitFlatTeamSettingKey[];

export const BrandKitFlatTeamSettingCatalogDefinitions =
  BrandKitFlatTeamSettingKeys.map((key) => {
    const definition = BrandKitRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: BrandKitFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: {
        adapter: "teamSettings",
        key,
      } satisfies TeamSettingsSettingValueSource<BrandKitFlatTeamSettingKey>,
    };
  }) satisfies readonly SettingDefinition<
    BrandKitFlatTeamSettingKey,
    BrandKitFlatTeamSettingGroupId
  >[];

export const BrandKitFlatTeamSettingMetadataByKey = Object.fromEntries(
  BrandKitFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<BrandKitFlatTeamSettingKey, SettingMetadata>;
